import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import { Uploader } from 'rsuite'
import './custom-file-uploader.scss'
import { useAuth } from '../../../customHooks/useAuth'
import axios from '../../../utils/axios'
import { t } from '../../../utils/i18n'

export interface FileType {
  fileKey?: number | string
  url?: string
  blobFile?: File
  name?: string
  status?: 'inited' | 'uploading' | 'error' | 'finished'
  progress?: number
  // eslint-disable-next-line
  [key: string]: any
}

interface CustomUploaderProps {
  apiEndpoint: string // This should be a signed PUT URL
  height?: number
  message?: string | React.ReactElement
  draggable?: boolean
  accept?: string
  onSuccess?: (response: object, file: FileType) => void
  // eslint-disable-next-line
  onError?: (err: any) => void
  onChange?: (files: FileType[]) => void
}

export interface CustomUploaderRef {
  handleManualUpload: () => void
}

export const CustomUploader = forwardRef<CustomUploaderRef, CustomUploaderProps>(
  (
    {
      apiEndpoint,
      height = 200,
      message = t('upload.file.title') + t('upload.file.subtitle'),
      draggable = true,
      accept,
      onSuccess,
      onError,
      onChange,
    },
    ref
  ) => {
    const { user } = useAuth()
    const fileRef = useRef<FileType | null>(null)
    const [files, setFiles] = useState<FileType[]>([])

    useImperativeHandle(ref, () => ({
      handleManualUpload: async () => {
        const file = fileRef.current
        if (!file?.blobFile) return

        const fileName = encodeURIComponent(file.blobFile.name)
        const signedUrlEndpoint = `${apiEndpoint}?fileName=${fileName}`
        const config = {
          method: 'GET',
          url: signedUrlEndpoint,
          headers: {
            'x-user': user?.email,
            'x-access-token': user?.tokens?.accessToken?.token,
            'x-source': user?.isSSO ? 'sso' : null,
          },
        }
        try {
          // Step 1: GET signed PUT URL and filename
          const getResponse = await axios(config)

          const putUrl = getResponse?.data?.data?.url
          const signedFileName = getResponse?.data?.data?.fileName

          if (!putUrl) {
            throw new Error('Missing signed PUT URL')
          }

          // Step 2: PUT file to signed URL with 'url' as a custom header
          const putResponse = await fetch(putUrl, {
            method: 'PUT',
            headers: {
              'Content-Type': file.blobFile.type,
            },
            body: file.blobFile,
          })

          if (!putResponse.ok) {
            throw new Error(`Upload failed with status ${putResponse.status}`)
          }

          onSuccess?.({ fileName: signedFileName, response: await putResponse.text() }, file)
        } catch (err) {
          onError?.(err)
        }
      },
    }))

    return (
      <Uploader
        draggable={draggable}
        fileListVisible={false}
        autoUpload={false}
        action="" // No auto-upload URL, as we're handling manually
        accept={accept}
        fileList={files}
        onChange={(newFiles) => {
          fileRef.current = newFiles?.[0] || null
          setFiles(newFiles)
          onChange?.(newFiles)
        }}
        className="file-uploader label-input"
      >
        {files.length === 0 ? (
          <span style={{ height }} className="pre-upload">
            {message}
          </span>
        ) : (
          <span style={{ height }} className="on-upload form-label">
            {files[0]?.name}
          </span>
        )}
      </Uploader>
    )
  }
)

CustomUploader.displayName = 'CustomUploader'

export default CustomUploader
